import { useState, useEffect, useRef } from 'react';
import { userRequest } from '../utils/requestMethods';
import { Header, ButtonLoading, BackButton, SendOTP } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import { ImSpinner } from 'react-icons/im';
import { FiCheckCircle, FiAlertCircle, FiChevronDown, FiSearch, FiInfo } from 'react-icons/fi';
import { formatCurrency } from '../utils/currencyUtils';

const TRANSACTION_FEE = 50;
const VAT = TRANSACTION_FEE * 0.075; // 3.75

const BankSelect = ({ banks, value, onChange, disabled }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);
    const searchRef = useRef(null);

    const selected = banks.find(b => b.bank_code === value);
    const filtered = banks.filter(b =>
        b.bank_name.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        if (open) searchRef.current?.focus();
    }, [open]);

    const handleSelect = (bank) => {
        onChange(bank);
        setOpen(false);
        setSearch('');
    };

    return (
        <div ref={containerRef} className="relative mt-1">
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen(o => !o)}
                className="flex items-center justify-between w-full rounded-md bg-gray-100 border-transparent px-3 py-2 text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-700 focus:outline-none focus:bg-white focus:border-gray-500 disabled:opacity-50"
            >
                <span className={selected ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}>
                    {selected ? selected.bank_name : 'Select bank'}
                </span>
                <FiChevronDown className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 shadow-lg overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                        <FiSearch className="text-gray-400 shrink-0 text-sm" />
                        <input
                            ref={searchRef}
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search banks..."
                            className="flex-1 text-sm bg-transparent border-none outline-none text-gray-700 dark:text-gray-200 placeholder-gray-400"
                        />
                    </div>
                    <ul className="max-h-52 overflow-y-auto">
                        {filtered.length === 0 ? (
                            <li className="px-4 py-3 text-sm text-gray-400 text-center">No banks found</li>
                        ) : filtered.map(bank => (
                            <li
                                key={bank.bank_code}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSelect(bank);
                                }}
                                className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-indigo-50 dark:hover:bg-gray-700 ${
                                    bank.bank_code === value ? 'bg-indigo-50 text-indigo-600 font-medium dark:bg-gray-700' : 'text-gray-700 dark:text-gray-200'
                                }`}
                            >
                                {bank.bank_name}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

const Transfer = ({ history }) => {
    const { user, school } = useStateContext();

    const [banks, setBanks] = useState([]);
    const [bankCode, setBankCode] = useState('');
    const [accountName, setAccountName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [amount, setAmount] = useState('');
    const [categories, setCategories] = useState([]);
    const [category, setCategory] = useState('');
    const [narration, setNarration] = useState('');
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [resolving, setResolving] = useState(false);
    const [balance, setBalance] = useState(null);

    useEffect(() => {
        const fetchBalanceAndCategories = async () => {
            try {
                const [balRes, catRes] = await Promise.all([
                    userRequest.get(`banking/wallets/${school?._id}/balance`),
                    userRequest.get(`/schools/${school?._id}/finance/expense-categories`),
                ]);
                setBalance(balRes.data.data ?? balRes.data);
                const rawCat = catRes?.data?.data || catRes?.data;
                if (rawCat) {
                    const systemCats = Array.isArray(rawCat.system) ? rawCat.system : [];
                    const customCats = Array.isArray(rawCat.custom) ? rawCat.custom : [];
                    const combined = [...systemCats, ...customCats];
                    if (combined.length > 0) {
                        setCategories(combined);
                    } else if (Array.isArray(rawCat.categories)) {
                        setCategories(rawCat.categories);
                    } else if (Array.isArray(rawCat)) {
                        setCategories(rawCat);
                    }
                }
            } catch (err) {
                console.log(err);
            }
        };
        if (school?._id) fetchBalanceAndCategories();
    }, [school?._id]);

    useEffect(() => {
        const fetchBanks = async () => {
            try {
                const { data } = await userRequest.get('banking/banks');
                const result = data?.data?.result || [];
                const seen = new Set();
                const unique = result.filter(b => {
                    if (seen.has(b.bank_code)) return false;
                    seen.add(b.bank_code);
                    return true;
                });
                setBanks(unique);
            } catch (error) {
                console.log(error);
            }
        };
        fetchBanks();
    }, [user]);

    const resolveAccount = async (code, number) => {
        if (!code || !number) return;
        setResolving(true);
        setAccountName('');
        try {
            const { data } = await userRequest.post('banking/validate-account', {
                accounts: [{ bankCode: code, accountNumber: number }]
            });
            const result = data?.result?.[0];
            setAccountName(result?.beneficiaryName || '');
            if (!result?.beneficiaryName) {
                setError('Could not resolve account name.');
                setTimeout(() => setError(''), 5000);
            }
        } catch (error) {
            console.log(error);
            setError('Error, unable to resolve account!');
            setTimeout(() => setError(''), 5000);
        } finally {
            setResolving(false);
        }
    };

    const handleBankSelect = (bank) => {
        setBankCode(bank.bank_code);
        resolveAccount(bank.bank_code, accountNumber);
    };

    const handleAccountNumberBlur = () => {
        resolveAccount(bankCode, accountNumber);
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        if (!amount || !accountNumber || !bankCode || !accountName || !category || !otp) {
            setError('Please fill all the required fields, including Category.');
            setTimeout(() => setError(''), 5000);
            return;
        }
        setLoading(true);
        try {
            await userRequest.post('banking/transfers', {
                schoolId: school?._id,
                amount: Number(amount),
                destinationBankCode: bankCode,
                destinationAccountNumber: accountNumber,
                destinationAccountName: accountName,
                narration: narration || 'School Transfer',
                category,
                otp,
            });
            setSuccess('Transfer submitted successfully.');
            setTimeout(() => {
                setSuccess('');
                setAmount('');
                setAccountNumber('');
                setBankCode('');
                setAccountName('');
                setCategory('');
                setNarration('');
                setOtp('');
            }, 3000);
        } catch (error) {
            console.log(error);
            setError(error?.response?.data?.message || 'Transfer failed. Please try again.');
            setTimeout(() => setError(''), 5000);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="m-2 md:m-10 mt-24 shadow p-2 md:p-10 bg-white dark:bg-secondary-dark-bg rounded-3xl min-h-screen">
            <BackButton history={history} />
            <Header category="Pay Outs" title="Transfer Funds" />

            <div className="py-4 max-w-sm">
                {success && (
                    <div className="flex items-center gap-2 rounded-2xl bg-green-50 border border-green-200 px-4 py-3 mb-4">
                        <FiCheckCircle className="text-green-500 shrink-0" />
                        <p className="text-sm font-medium text-green-700">{success}</p>
                    </div>
                )}
                {error && (
                    <div className="flex items-center gap-2 rounded-2xl bg-red-50 border border-red-200 px-4 py-3 mb-4">
                        <FiAlertCircle className="text-red-500 shrink-0" />
                        <p className="text-sm font-medium text-red-700">{error}</p>
                    </div>
                )}

                <div className="rounded-2xl border border-gray-200 px-5 py-5">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Transfer Details</p>
                    <form onSubmit={submitHandler}>
                        <div className="grid grid-cols-1 gap-5">
                            <SendOTP school={school} />

                            <label className="block">
                                <span className="text-gray-700 dark:text-gray-200">Amount (₦)</span>
                                <input
                                    type="number"
                                    value={amount}
                                    onChange={e => setAmount(e.target.value)}
                                    placeholder="e.g. 5000"
                                    className="mt-1 block w-full rounded-md bg-gray-100 border-transparent focus:border-gray-500 focus:bg-white focus:ring-0"
                                />
                                {amount > 0 && (() => {
                                    const amountNum = parseFloat(amount) || 0;
                                    const total = amountNum + TRANSACTION_FEE + VAT;
                                    const availableBalance = parseFloat(balance?.availableBalance) || 0;
                                    const sufficient = availableBalance >= total;
                                    return (
                                        <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 space-y-2 text-sm">
                                            <div className="flex justify-between text-gray-600">
                                                <span>Transfer amount</span>
                                                <span>{formatCurrency(amountNum)}</span>
                                            </div>
                                            <div className="flex justify-between text-gray-600">
                                                <span className="flex items-center gap-1">
                                                    Transaction fee
                                                    <FiInfo className="text-gray-400 text-xs" title="Flat fee per transfer" />
                                                </span>
                                                <span>{formatCurrency(TRANSACTION_FEE)}</span>
                                            </div>
                                            <div className="flex justify-between text-gray-600">
                                                <span className="flex items-center gap-1">
                                                    VAT (7.5%)
                                                    <FiInfo className="text-gray-400 text-xs" title="7.5% VAT on transaction fee" />
                                                </span>
                                                <span>{formatCurrency(VAT)}</span>
                                            </div>
                                            <div className="border-t border-gray-200 pt-2 flex justify-between font-semibold text-gray-800">
                                                <span>Total deducted</span>
                                                <span>{formatCurrency(total)}</span>
                                            </div>
                                            {balance && (
                                                <div className={`flex justify-between text-xs pt-1 ${sufficient ? 'text-green-600' : 'text-red-500'}`}>
                                                    <span>Available balance</span>
                                                    <span>{formatCurrency(availableBalance)}</span>
                                                </div>
                                            )}
                                            {balance && !sufficient && (
                                                <div className="flex items-center gap-1.5 text-xs text-red-500 pt-1">
                                                    <FiAlertCircle className="shrink-0" />
                                                    Insufficient balance for this transfer
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </label>

                            <label className="block">
                                <span className="text-gray-700 dark:text-gray-200">Account Number</span>
                                <input
                                    type="text"
                                    value={accountNumber}
                                    onChange={e => setAccountNumber(e.target.value)}
                                    onBlur={handleAccountNumberBlur}
                                    placeholder="Enter 10-digit account number"
                                    className="mt-1 block w-full rounded-md bg-gray-100 border-transparent focus:border-gray-500 focus:bg-white focus:ring-0"
                                />
                            </label>

                            <label className="block">
                                <span className="text-gray-700 dark:text-gray-200">Bank Name</span>
                                <BankSelect
                                    banks={banks}
                                    value={bankCode}
                                    onChange={handleBankSelect}
                                    disabled={banks.length === 0}
                                />
                            </label>

                            <label className="block">
                                <span className="text-gray-700 dark:text-gray-200">
                                    Account Name
                                    {resolving && (
                                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-indigo-500 font-normal">
                                            <ImSpinner className="animate-spin" /> Verifying...
                                        </span>
                                    )}
                                </span>
                                <input
                                    type="text"
                                    value={accountName}
                                    readOnly
                                    placeholder="Auto-filled after verification"
                                    className="mt-1 block w-full rounded-md bg-gray-100 border-transparent text-gray-500 cursor-not-allowed"
                                />
                            </label>

                            <label className="block">
                                <span className="text-gray-700 dark:text-gray-200">Category <span className="text-red-500">*</span></span>
                                <select
                                    value={category}
                                    onChange={e => setCategory(e.target.value)}
                                    className="mt-1 block w-full rounded-md bg-gray-100 border-transparent focus:border-gray-500 focus:bg-white focus:ring-0 text-sm"
                                >
                                    <option value="">Select category</option>
                                    {categories.map(c => (
                                        <option key={c.slug} value={c.slug}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className="block">
                                <span className="text-gray-700 dark:text-gray-200">Narration <span className="text-gray-400 font-normal">(optional)</span></span>
                                <input
                                    type="text"
                                    value={narration}
                                    onChange={e => setNarration(e.target.value)}
                                    placeholder="e.g. Staff salary payment"
                                    className="mt-1 block w-full rounded-md bg-gray-100 border-transparent focus:border-gray-500 focus:bg-white focus:ring-0"
                                />
                            </label>

                            <label className="block">
                                <span className="text-gray-700 dark:text-gray-200">OTP</span>
                                <input
                                    type="text"
                                    value={otp}
                                    onChange={e => setOtp(e.target.value)}
                                    placeholder="Enter generated OTP"
                                    className="mt-1 block w-full rounded-md bg-gray-100 border-transparent focus:border-gray-500 focus:bg-white focus:ring-0"
                                />
                            </label>

                            <div className="text-right">
                                {loading ? (
                                    <ButtonLoading styles="inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-indigo-400" />
                                ) : (
                                    <button
                                        type="submit"
                                        disabled={resolving || !accountName}
                                        className="inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Send Funds
                                    </button>
                                )}
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Transfer;
